import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import Button from '../../Atoms/Button';

import {
  Overlay,
  Dialog,
  Title,
  Description,
  Actions,
  CancelButton,
} from './styles';

interface ConfirmProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Se `true`, a ação é destrutiva (o botão de confirmar vira perigo). */
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmação acessível do admin — substitui `window.confirm`.
 * É um `<dialog>` de verdade: fecha no ESC, prende o foco e devolve o foco ao
 * botão que o abriu ao fechar.
 */
const Confirm: React.FC<ConfirmProps> = ({
  open,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    // Foca o botão de confirmar (ou de cancelar, se perigoso) ao abrir.
    const target = danger ? '[data-cancel]' : '[data-confirm]';
    dialogRef.current?.querySelector<HTMLButtonElement>(target)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, danger, onCancel]);

  if (!open) return null;

  return createPortal(
    <Overlay
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <Dialog
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
      >
        <Title id="confirm-title">{title}</Title>
        {description && <Description>{description}</Description>}
        <Actions>
          <CancelButton type="button" data-cancel onClick={onCancel}>
            {cancelLabel}
          </CancelButton>
          <Button
            type="button"
            data-confirm
            loading={loading}
            customStyle={danger ? 'danger' : undefined}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </Actions>
      </Dialog>
    </Overlay>,
    document.body
  );
};

export default Confirm;
